import { useState, useEffect, useCallback } from "react";
import { useToast } from "@/components/base/Toast";
import { fetchApi } from "@/services/apiService";

// ─── Tailwind class → hex (built-in templates use Tailwind classes) ──
const twToHex: Record<string, string> = {
  "bg-green-500": "#22c55e",
  "bg-blue-500": "#3b82f6",
  "bg-indigo-500": "#6366f1",
  "bg-amber-500": "#f59e0b",
  "bg-orange-500": "#f97316",
  "bg-red-500": "#ef4444",
  "bg-purple-500": "#a855f7",
  "bg-teal-500": "#14b8a6",
  "bg-pink-500": "#ec4899",
  "bg-cyan-500": "#06b6d4",
  "bg-green-600": "#16a34a",
};
const resolveColor = (c: string) => twToHex[c] ?? c;

// ─── Types ────────────────────────────────────────────────────
interface StageExtraField {
  id: string;
  label: string;
  type: string;
  required: boolean;
  options?: string[];
}

export interface Stage {
  id: string;
  name: string;
  color: string;       // hex
  required: boolean;
  extraFields?: StageExtraField[];
}

interface IndustryExample {
  id: string;
  name: string;
  icon: string;
  iconBg: string;
  iconColor: string;
  isCustom: boolean;
  stageList: { name: string; color: string; required: boolean }[];
}

type Props = {
  stages: Stage[];
  onStagesChange: (stages: Stage[]) => void;
  onSave: (stages: Stage[]) => Promise<void>;
};

// ─── Constants ────────────────────────────────────────────────
const defaultStages: Stage[] = [
  { id: "tmp-1", name: "New Inquiry", color: "#22c55e", required: true },
  { id: "tmp-2", name: "Site Visit Scheduled", color: "#3b82f6", required: true },
  { id: "tmp-3", name: "Site Visit Done", color: "#6366f1", required: true },
  { id: "tmp-4", name: "Proposal Sent", color: "#f59e0b", required: true },
  { id: "tmp-5", name: "Negotiation", color: "#f97316", required: true },
  { id: "tmp-6", name: "Booking Done", color: "#22c55e", required: true },
  { id: "tmp-7", name: "Lost", color: "#ef4444", required: false },
];

const colorOptions = ["#22c55e", "#3b82f6", "#6366f1", "#f59e0b", "#f97316", "#ef4444", "#a855f7", "#ec4899", "#14b8a6", "#64748b"];
const fieldTypeOptions = ["Text Input", "Number", "Currency (₹)", "Date Picker", "Time Picker", "Dropdown", "Textarea", "Toggle"];

// ✅ Static built-ins — always available even if API is offline
const builtInExamples: IndustryExample[] = [
  {
    id: "real-estate", name: "Real Estate", icon: "ri-building-4-line",
    iconBg: "bg-indigo-100", iconColor: "text-indigo-600", isCustom: false,
    stageList: [
      { name: "New Lead", color: "#22c55e", required: true },
      { name: "Site Visit", color: "#3b82f6", required: true },
      { name: "Proposal", color: "#6366f1", required: true },
      { name: "Negotiation", color: "#f59e0b", required: false },
      { name: "Booking", color: "#a855f7", required: false },
      { name: "Documentation", color: "#14b8a6", required: false },
      { name: "Closed Won", color: "#16a34a", required: false },
    ],
  },
  {
    id: "manufacturing", name: "Manufacturing", icon: "ri-building-2-line",
    iconBg: "bg-amber-100", iconColor: "text-amber-600", isCustom: false,
    stageList: [
      { name: "Inquiry", color: "#22c55e", required: true },
      { name: "Sample", color: "#3b82f6", required: false },
      { name: "Quote", color: "#6366f1", required: true },
      { name: "PO", color: "#f59e0b", required: false },
      { name: "Production", color: "#a855f7", required: false },
      { name: "Delivery", color: "#16a34a", required: false },
    ],
  },
  {
    id: "education", name: "Education", icon: "ri-graduation-cap-line",
    iconBg: "bg-green-100", iconColor: "text-green-600", isCustom: false,
    stageList: [
      { name: "Inquiry", color: "#22c55e", required: true },
      { name: "Counseling", color: "#3b82f6", required: false },
      { name: "Application", color: "#6366f1", required: false },
      { name: "Enrolled", color: "#a855f7", required: false },
      { name: "Alumni", color: "#16a34a", required: false },
    ],
  },
  {
    id: "healthcare", name: "Healthcare", icon: "ri-heart-line",
    iconBg: "bg-red-100", iconColor: "text-red-600", isCustom: false,
    stageList: [
      { name: "Inquiry", color: "#22c55e", required: true },
      { name: "Consultation", color: "#3b82f6", required: true },
      { name: "Diagnosis", color: "#6366f1", required: false },
      { name: "Treatment", color: "#f59e0b", required: false },
      { name: "Follow-up", color: "#a855f7", required: false },
      { name: "Discharged", color: "#16a34a", required: false },
    ],
  },
  {
    id: "saas", name: "SaaS / IT", icon: "ri-code-line",
    iconBg: "bg-purple-100", iconColor: "text-purple-600", isCustom: false,
    stageList: [
      { name: "Lead", color: "#22c55e", required: true },
      { name: "Demo", color: "#3b82f6", required: false },
      { name: "Trial", color: "#6366f1", required: false },
      { name: "Proposal", color: "#f59e0b", required: false },
      { name: "Negotiation", color: "#f97316", required: false },
      { name: "Closed Won", color: "#16a34a", required: false },
      { name: "Onboarding", color: "#14b8a6", required: false },
    ],
  },
  {
    id: "retail", name: "Retail", icon: "ri-shopping-bag-3-line",
    iconBg: "bg-pink-100", iconColor: "text-pink-600", isCustom: false,
    stageList: [
      { name: "Inquiry", color: "#22c55e", required: true },
      { name: "Quote", color: "#3b82f6", required: false },
      { name: "Sample", color: "#6366f1", required: false },
      { name: "Order", color: "#f59e0b", required: false },
      { name: "Repeat", color: "#16a34a", required: false },
    ],
  },
];

const tempId = () => `tmp-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;

export default function PipelineStageBuilder({ stages: propStages, onStagesChange, onSave }: Props) {
  const { showToast } = useToast();

  const [stages, setStages] = useState<Stage[]>(propStages.length > 0 ? propStages : defaultStages);
  const [editId, setEditId] = useState<string | null>(null);
  const [editName, setEditName] = useState("");
  const [openColorPicker, setOpenColorPicker] = useState<string | null>(null);
  const [draggedIdx, setDraggedIdx] = useState<number | null>(null);
  const [showExamples, setShowExamples] = useState(false);
  const [confirmIndustry, setConfirmIndustry] = useState<IndustryExample | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  // ✅ Dynamic industry examples = built-ins + custom from API
  const [allExamples, setAllExamples] = useState<IndustryExample[]>(builtInExamples);
  const [examplesLoading, setExamplesLoading] = useState(false);

  // Extra fields — keyed by stage.id
  const [extraFieldsMap, setExtraFieldsMap] = useState<Record<string, StageExtraField[]>>({});
  const [expandedStage, setExpandedStage] = useState<string | null>(null);
  const [addingFieldForStage, setAddingFieldForStage] = useState<string | null>(null);
  const [newFieldLabel, setNewFieldLabel] = useState("");
  const [newFieldType, setNewFieldType] = useState("Text Input");
  const [newFieldRequired, setNewFieldRequired] = useState(false);
  const [newFieldOptions, setNewFieldOptions] = useState<string[]>([]);
  const [newFieldOptionInput, setNewFieldOptionInput] = useState("");
  const [editingField, setEditingField] = useState<{ stageId: string; fieldId: string } | null>(null);
  const [editOptionInput, setEditOptionInput] = useState("");

  // ✅ Sync from API
  useEffect(() => {
    setStages(propStages);
    const map: Record<string, StageExtraField[]> = {};
    propStages.forEach((s) => { if (s.extraFields) map[s.id] = s.extraFields; });
    setExtraFieldsMap(map);
  }, [propStages]);

  // ✅ Fetch templates from API using fetchApi
  const fetchExamples = useCallback(async () => {
    setExamplesLoading(true);
    try {
      const res = await fetchApi<any>("/industry-templates/get-all", undefined, { data: [] });
      const customTemplates: IndustryExample[] = (res?.data || []).map((t: any) => ({
        id: String(t.id),
        name: t.name,
        icon: t.icon || "ri-building-4-line",
        iconBg: t.iconBg || "bg-gray-100",
        iconColor: t.iconColor || "text-gray-600",
        isCustom: true,
        stageList: (t.stageList || []).map((s: any) => ({
          name: s.name,
          color: resolveColor(s.color || "#64748b"),
          required: s.required ?? false,
        })),
      }));
      setAllExamples(customTemplates.length > 0 ? customTemplates : builtInExamples);
    } catch {
      // keep built-ins if API fails
    } finally {
      setExamplesLoading(false);
    }
  }, []);

  // Fetch when examples panel opens
  useEffect(() => {
    if (showExamples) fetchExamples();
  }, [showExamples, fetchExamples]);

  // ─── Stage helpers ─────────────────────────────────────────
  const updateStages = (u: Stage[]) => { setStages(u); onStagesChange(u); };
  const handleNameEdit = (s: Stage) => { setEditId(s.id); setEditName(s.name); };
  const handleNameSave = (id: string) => {
    if (editName.trim()) updateStages(stages.map((s) => s.id === id ? { ...s, name: editName.trim() } : s));
    setEditId(null);
  };
  const handleToggleRequired = (id: string) => updateStages(stages.map((s) => s.id === id ? { ...s, required: !s.required } : s));
  const handleColorChange = (id: string, color: string) => { updateStages(stages.map((s) => s.id === id ? { ...s, color } : s)); setOpenColorPicker(null); };
  const handleDelete = (id: string) => { if (stages.length <= 2) return; updateStages(stages.filter((s) => s.id !== id)); };
  const handleAdd = () => {
    if (stages.length >= 10) return;
    updateStages([...stages, { id: tempId(), name: "New Stage", color: "#64748b", required: true }]);
  };

  // Drag-and-drop
  const handleDragStart = (idx: number) => setDraggedIdx(idx);
  const handleDragOver = (e: React.DragEvent, idx: number) => {
    e.preventDefault();
    if (draggedIdx === null || draggedIdx === idx) return;
    const u = [...stages]; const [m] = u.splice(draggedIdx, 1); u.splice(idx, 0, m);
    updateStages(u); setDraggedIdx(idx);
  };
  const handleDragEnd = () => setDraggedIdx(null);

  // ✅ Apply industry example
  const confirmUseExample = () => {
    if (!confirmIndustry) return;
    const newStages: Stage[] = confirmIndustry.stageList.map((s, i) => ({
      id: tempId(), name: s.name, color: s.color, required: s.required,
    }));
    updateStages(newStages);
    confirmSwitch(confirmIndustry);
    setConfirmIndustry(null);
    setShowExamples(false);
    showToast(`${confirmIndustry.name} stages applied. Edit as needed.`, "success");
  };

  // Extra fields
  const toggleExpand = (id: string) => { setExpandedStage(expandedStage === id ? null : id); setAddingFieldForStage(null); setEditingField(null); };
  const getFields = (id: string) => extraFieldsMap[id] || [];

  const startAddField = (id: string) => {
    setAddingFieldForStage(id);
    setNewFieldLabel("");
    setNewFieldType("Text Input");
    setNewFieldRequired(false);
    setNewFieldOptions([]);
    setNewFieldOptionInput("");
  };

  const handleAddFieldSave = () => {
    if (!addingFieldForStage || !newFieldLabel.trim()) return;
    const newField: StageExtraField = {
      id: tempId(), label: newFieldLabel.trim(),
      type: newFieldType.toLowerCase().replace(/ /g, "_").replace("currency_(₹)", "currency").replace("date_picker", "date").replace("time_picker", "time").replace("text_input", "text"),
      required: newFieldRequired,
      options: newFieldType === "Dropdown" ? newFieldOptions : undefined,
    };
    console.log("stagesstagesstages=>", newField)
    setExtraFieldsMap({ ...extraFieldsMap, [addingFieldForStage]: [...getFields(addingFieldForStage), newField] });
    setAddingFieldForStage(null);
    showToast(`Field "${newFieldLabel}" added`, "success");
  };

  const handleAddOption = () => { if (!newFieldOptionInput.trim()) return; setNewFieldOptions([...newFieldOptions, newFieldOptionInput.trim()]); setNewFieldOptionInput(""); };
  const handleRemoveOption = (i: number) => setNewFieldOptions(newFieldOptions.filter((_, idx) => idx !== i));
  const handleDeleteField = (sid: string, fid: string) => setExtraFieldsMap({ ...extraFieldsMap, [sid]: getFields(sid).filter((f) => f.id !== fid) });
  const handleToggleFieldReq = (sid: string, fid: string) => setExtraFieldsMap({ ...extraFieldsMap, [sid]: getFields(sid).map((f) => f.id === fid ? { ...f, required: !f.required } : f) });
  const startEditField = (sid: string, fid: string) => { setEditingField({ stageId: sid, fieldId: fid }); setEditOptionInput(""); };
  const handleEditOptionAdd = () => {
    if (!editOptionInput.trim() || !editingField) return;
    const { stageId: sid, fieldId: fid } = editingField;
    setExtraFieldsMap({ ...extraFieldsMap, [sid]: getFields(sid).map((f) => f.id === fid ? { ...f, options: [...(f.options || []), editOptionInput.trim()] } : f) });
    setEditOptionInput("");
  };
  const handleEditOptionRemove = (i: number) => {
    if (!editingField) return;
    const { stageId: sid, fieldId: fid } = editingField;
    setExtraFieldsMap({ ...extraFieldsMap, [sid]: getFields(sid).map((f) => f.id === fid ? { ...f, options: (f.options || []).filter((_, idx) => idx !== i) } : f) });
  };

  const fieldTypeLabel = (type: string) => {
    const m: Record<string, string> = { text: "Text Input", number: "Number", currency: "Currency (₹)", date: "Date Picker", time: "Time Picker", dropdown: "Dropdown", textarea: "Textarea", toggle: "Toggle" };
    return m[type] || type;
  };

  const handleSaveAll = async () => {
    setIsSaving(true);
    try { await onSave(stages.map((s) => ({ ...s, extraFields: getFields(s.id) }))); }
    catch { } finally { setIsSaving(false); }
  };

  const confirmSwitch = async (newStages) => {
    if (!newStages) return;
    try {
      await axios.patch(
        `${API}/industry-templates/switch`,
        {
          type: newStages.isCustom ? "custom" : "builtin",
          key: newStages.id,
          stages: newStages.stageList,
          fields: newStages.fieldList,
        },
        { headers: authHeader() }
      );
    } catch {
      showToast("Failed to switch industry template", "error");
    }
  };

  return (
    <div className="bg-white rounded-xl border border-gray-200 p-5">
      {/* Header */}
      <div className="flex items-center justify-between mb-1">
        <div>
          <h2 className="text-base font-semibold text-gray-900">Pipeline Stages</h2>
          <p className="text-sm text-gray-500">Define stages in your sales pipeline. Drag to reorder.</p>
        </div>
        {/* <button
          onClick={() => setShowExamples(!showExamples)}
          className="text-sm text-indigo-600 hover:text-indigo-700 font-medium whitespace-nowrap transition-colors"
        >
          {showExamples ? "Hide Examples" : "Industry Examples"}
        </button> */}
      </div>

      {/* ✅ Dynamic Industry Examples */}
      {/* {showExamples && (
        <div className="mt-3 mb-4 p-4 bg-indigo-50 rounded-xl border border-indigo-100">
          <div className="flex items-center justify-between mb-3">
            <p className="text-xs font-semibold text-indigo-700 uppercase tracking-wider">
              Pre-built stage templates
            </p>
            {examplesLoading && (
              <span className="text-xs text-indigo-400 flex items-center gap-1">
                <span className="w-3 h-3 border border-indigo-400 border-t-transparent rounded-full animate-spin" />
                Loading...
              </span>
            )}
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {allExamples.map((ex) => (
              <button
                key={ex.id}
                onClick={() => setConfirmIndustry(ex)}
                className="text-left p-3 rounded-xl border border-indigo-200 bg-white hover:border-indigo-400 hover:shadow-sm transition-all cursor-pointer group"
              >
                <div className="flex items-center gap-2 mb-2">
                  <div className={`w-7 h-7 rounded-lg ${ex.iconBg} flex items-center justify-center flex-shrink-0`}>
                    <i className={`${ex.icon} text-sm ${ex.iconColor}`} />
                  </div>
                  <p className="text-xs font-semibold text-gray-900 flex-1 truncate">{ex.name}</p>
                  {ex.isCustom && (
                    <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-purple-100 text-purple-700 font-medium whitespace-nowrap">
                      Custom
                    </span>
                  )}
                </div>

                <div className="flex items-center gap-1 flex-wrap">
                  {ex.stageList.slice(0, 6).map((s, i) => (
                    <div key={i} className="flex items-center gap-1">
                      {i > 0 && <span className="text-gray-300 text-[10px]">→</span>}
                      <div className="flex items-center gap-0.5">
                        <div className="w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: s.color }} />
                        <span className="text-[10px] text-gray-500 whitespace-nowrap">{s.name}</span>
                      </div>
                    </div>
                  ))}
                  {ex.stageList.length > 6 && (
                    <span className="text-[10px] text-gray-400">+{ex.stageList.length - 6} more</span>
                  )}
                </div>

                <p className="text-[11px] text-indigo-600 font-medium mt-2 group-hover:underline">
                  Use this ({ex.stageList.length} stages) →
                </p>
              </button>
            ))}
          </div>
        </div>
      )} */}

      {/* Stages list */}
      <div className="space-y-2 mt-4">
        {stages.map((stage, idx) => {
          const fields = getFields(stage.id);
          const expanded = expandedStage === stage.id;
          return (
            <div
              key={stage.id}
              draggable
              onDragStart={() => handleDragStart(idx)}
              onDragOver={(e) => handleDragOver(e, idx)}
              onDragEnd={handleDragEnd}
              className={`border border-gray-200 rounded-xl transition-all ${draggedIdx === idx ? "opacity-50 scale-[0.98]" : ""}`}
            >
              {/* Stage row */}
              <div className="flex items-center gap-3 px-4 py-3">
                <i className="ri-drag-move-2-line text-gray-300 cursor-grab text-lg flex-shrink-0" />

                {/* Color picker */}
                <div className="relative flex-shrink-0">
                  <button
                    onClick={() => setOpenColorPicker(openColorPicker === stage.id ? null : stage.id)}
                    className="w-5 h-5 rounded-full border-2 border-white shadow-sm ring-1 ring-gray-200 transition-transform hover:scale-110"
                    style={{ backgroundColor: stage.color }}
                  />
                  {openColorPicker === stage.id && (
                    <div className="absolute left-0 top-7 z-20 bg-white rounded-xl border border-gray-200 shadow-lg p-2.5 flex flex-wrap gap-2" style={{ width: 168 }}>
                      {colorOptions.map((c) => (
                        <button
                          key={c}
                          onClick={() => handleColorChange(stage.id, c)}
                          className={`w-7 h-7 rounded-full transition-transform hover:scale-110 ${stage.color === c ? "ring-2 ring-offset-1 ring-gray-800" : ""}`}
                          style={{ backgroundColor: c }}
                        />
                      ))}
                    </div>
                  )}
                </div>

                {/* Name */}
                {editId === stage.id ? (
                  <input
                    autoFocus value={editName}
                    onChange={(e) => setEditName(e.target.value)}
                    onBlur={() => handleNameSave(stage.id)}
                    onKeyDown={(e) => { if (e.key === "Enter") handleNameSave(stage.id); if (e.key === "Escape") setEditId(null); }}
                    className="flex-1 px-2 py-1 rounded border border-indigo-300 text-sm font-medium focus:outline-none focus:ring-2 focus:ring-indigo-200"
                  />
                ) : (
                  <button
                    onClick={() => handleNameEdit(stage)}
                    className="flex-1 text-left text-sm font-medium text-gray-900 hover:text-indigo-600 transition-colors truncate"
                  >
                    {stage.name}
                  </button>
                )}

                {/* Required badge */}
                <button
                  onClick={() => handleToggleRequired(stage.id)}
                  className={`text-[11px] font-medium px-2 py-0.5 rounded-full border transition-colors whitespace-nowrap flex-shrink-0 ${stage.required ? "bg-indigo-50 text-indigo-700 border-indigo-200" : "bg-gray-50 text-gray-400 border-gray-200"
                    }`}
                >
                  {stage.required ? "Required" : "Optional"}
                </button>

                {/* Fields toggle */}
                <button
                  onClick={() => toggleExpand(stage.id)}
                  className="flex items-center gap-1 text-xs text-gray-500 hover:text-indigo-600 transition-colors flex-shrink-0"
                >
                  <i className={`ri-survey-line ${expanded ? "text-indigo-600" : ""}`} />
                  <span>{fields.length > 0 ? `${fields.length} field${fields.length > 1 ? "s" : ""}` : "Fields"}</span>
                  <i className={`ri-arrow-${expanded ? "up" : "down"}-s-line`} />
                </button>

                {/* Delete */}
                <button
                  onClick={() => handleDelete(stage.id)}
                  disabled={stages.length <= 2}
                  className="w-7 h-7 flex items-center justify-center rounded-lg text-gray-300 hover:text-red-500 hover:bg-red-50 transition-colors disabled:opacity-30 disabled:cursor-not-allowed flex-shrink-0"
                >
                  <i className="ri-delete-bin-line text-sm" />
                </button>
              </div>

              {/* Extra fields panel */}
              {expanded && (
                <div className="border-t border-gray-100 px-4 py-3 bg-gray-50/50 rounded-b-xl">
                  {fields.length === 0 && !addingFieldForStage && (
                    <button
                      onClick={() => startAddField(stage.id)}
                      className="w-full py-3 border-2 border-dashed border-gray-200 rounded-lg text-center text-indigo-600 text-xs cursor-pointer hover:bg-indigo-50 transition-colors"
                    >
                      + Add a field to this stage
                    </button>
                  )}

                  {fields.map((field) => (
                    <div key={field.id} className="bg-white border border-gray-100 rounded-lg px-3 py-2 mb-2">
                      <div className="flex items-center gap-2">
                        <span className="text-xs font-medium text-gray-800 flex-1">{field.label}</span>
                        <span className="text-[11px] bg-gray-100 text-gray-500 px-2 py-0.5 rounded-full">{fieldTypeLabel(field.type)}</span>
                        <button onClick={() => handleToggleFieldReq(stage.id, field.id)}
                          className={`text-[11px] px-1.5 py-0.5 rounded-full ${field.required ? "text-indigo-600" : "text-gray-400"}`}>
                          {field.required ? "Req" : "Opt"}
                        </button>
                        {field.type === "dropdown" && (
                          <button onClick={() => startEditField(stage.id, field.id)}
                            className="w-5 h-5 flex items-center justify-center text-gray-400 hover:text-indigo-600">
                            <i className="ri-pencil-line text-xs" />
                          </button>
                        )}
                        <button onClick={() => handleDeleteField(stage.id, field.id)}
                          className="w-5 h-5 flex items-center justify-center text-gray-300 hover:text-red-500">
                          <i className="ri-close-line text-xs" />
                        </button>
                      </div>

                      {editingField?.stageId === stage.id && editingField?.fieldId === field.id && (
                        <div className="mt-2 pt-2 border-t border-gray-100">
                          <div className="flex flex-wrap gap-1 mb-1.5">
                            {(field.options || []).map((opt, oi) => (
                              <span key={oi} className="inline-flex items-center gap-1 px-2 py-0.5 bg-indigo-50 text-indigo-700 text-xs rounded-full">
                                {opt}
                                <button onClick={() => handleEditOptionRemove(oi)} className="text-indigo-400 hover:text-red-500"><i className="ri-close-line" /></button>
                              </span>
                            ))}
                          </div>
                          <div className="flex items-center gap-2">
                            <input value={editOptionInput} onChange={(e) => setEditOptionInput(e.target.value)}
                              onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); handleEditOptionAdd(); } }}
                              placeholder="Add option..." className="flex-1 text-xs border border-gray-200 rounded px-2 py-1 outline-none focus:border-indigo-400" />
                            <button onClick={handleEditOptionAdd} className="text-xs text-indigo-600 font-medium whitespace-nowrap">Add</button>
                            <button onClick={() => setEditingField(null)} className="text-xs text-gray-400 whitespace-nowrap">Done</button>
                          </div>
                        </div>
                      )}
                    </div>
                  ))}

                  {/* Add field form */}
                  {addingFieldForStage === stage.id && (
                    <div className="bg-white border border-indigo-200 rounded-lg p-3 mt-2">
                      <p className="text-xs font-semibold text-gray-800 mb-2">New Field</p>
                      <div className="grid grid-cols-2 gap-2 mb-2">
                        <div>
                          <label className="block text-[11px] text-gray-600 mb-1">Label</label>
                          <input value={newFieldLabel} onChange={(e) => setNewFieldLabel(e.target.value)}
                            placeholder="e.g. Visit Date"
                            className="w-full text-xs border border-gray-200 rounded px-2 py-1.5 outline-none focus:border-indigo-400" />
                        </div>
                        <div>
                          <label className="block text-[11px] text-gray-600 mb-1">Type</label>
                          <select value={newFieldType} onChange={(e) => setNewFieldType(e.target.value)}
                            className="w-full text-xs border border-gray-200 rounded px-2 py-1.5 outline-none bg-white focus:border-indigo-400">
                            {fieldTypeOptions.map((t) => <option key={t} value={t}>{t}</option>)}
                          </select>
                        </div>
                      </div>
                      {newFieldType === "Dropdown" && (
                        <div className="mb-2">
                          <div className="flex flex-wrap gap-1 mb-1">
                            {newFieldOptions.map((opt, oi) => (
                              <span key={oi} className="inline-flex items-center gap-1 px-2 py-0.5 bg-indigo-50 text-indigo-700 text-xs rounded-full">
                                {opt}
                                <button onClick={() => handleRemoveOption(oi)}><i className="ri-close-line" /></button>
                              </span>
                            ))}
                          </div>
                          <div className="flex gap-1.5">
                            <input value={newFieldOptionInput} onChange={(e) => setNewFieldOptionInput(e.target.value)}
                              onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); handleAddOption(); } }}
                              placeholder="Add option..." className="flex-1 text-xs border border-gray-200 rounded px-2 py-1 outline-none focus:border-indigo-400" />
                            <button onClick={handleAddOption} className="text-xs text-indigo-600 font-medium px-2">Add</button>
                          </div>
                        </div>
                      )}
                      <label className="flex items-center gap-2 mb-2 cursor-pointer">
                        <input type="checkbox" checked={newFieldRequired} onChange={(e) => setNewFieldRequired(e.target.checked)} className="w-3.5 h-3.5 accent-indigo-600" />
                        <span className="text-xs text-gray-600">Required field</span>
                      </label>
                      <div className="flex gap-2">
                        <button onClick={handleAddFieldSave} disabled={!newFieldLabel.trim()}
                          className="px-3 py-1.5 text-xs font-medium text-white bg-indigo-600 hover:bg-indigo-700 rounded-lg disabled:opacity-50 whitespace-nowrap">
                          Add Field
                        </button>
                        <button onClick={() => setAddingFieldForStage(null)}
                          className="px-3 py-1.5 text-xs text-gray-600 hover:bg-gray-100 rounded-lg whitespace-nowrap">
                          Cancel
                        </button>
                      </div>
                    </div>
                  )}

                  {!addingFieldForStage && fields.length > 0 && (
                    <button onClick={() => startAddField(stage.id)}
                      className="w-full mt-2 border-2 border-dashed border-gray-200 rounded-lg py-2 text-center text-indigo-600 text-xs hover:bg-indigo-50 cursor-pointer transition-colors">
                      + Add Field
                    </button>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Bottom row */}
      <div className="mt-4 flex items-center justify-between">
        <button onClick={handleAdd} disabled={stages.length >= 10}
          className="text-sm font-medium text-indigo-600 hover:text-indigo-700 transition-colors whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed">
          + Add Stage
        </button>
        <p className="text-xs text-gray-400">{stages.length} of 10 stages used</p>
      </div>

      {/* Save button */}
      <div className="mt-4 pt-4 border-t border-gray-200">
        <button onClick={handleSaveAll} disabled={isSaving}
          className="w-full py-2.5 rounded-lg bg-indigo-600 hover:bg-indigo-700 disabled:opacity-60 text-white text-sm font-semibold transition-all active:scale-[0.98] cursor-pointer whitespace-nowrap flex items-center justify-center gap-2">
          {isSaving
            ? <><span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />Saving...</>
            : "Save Pipeline Stages"}
        </button>
      </div>

      {/* Confirm modal */}
      {confirmIndustry && (
        <>
          <div className="fixed inset-0 bg-black/20 z-40" onClick={() => setConfirmIndustry(null)} />
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <div className="bg-white rounded-xl border border-gray-200 shadow-lg w-full max-w-sm">
              <div className="p-5">
                <div className={`w-12 h-12 rounded-full ${confirmIndustry.iconBg} flex items-center justify-center mx-auto mb-4`}>
                  <i className={`${confirmIndustry.icon} text-xl ${confirmIndustry.iconColor}`} />
                </div>
                <h3 className="text-base font-semibold text-gray-900 text-center">
                  Replace current stages with <span className="text-indigo-600">{confirmIndustry.name}</span>?
                </h3>
                <p className="text-sm text-gray-500 text-center mt-1 mb-3">You can edit them after.</p>

                {/* ✅ Stage preview with real colors */}
                <div className="bg-gray-50 rounded-lg p-3 flex flex-wrap gap-1.5 justify-center">
                  {confirmIndustry.stageList.map((s, i) => (
                    <div key={i} className="flex items-center gap-1 text-xs text-gray-600">
                      {i > 0 && <span className="text-gray-300">→</span>}
                      <div className="w-2 h-2 rounded-full" style={{ backgroundColor: s.color }} />
                      <span>{s.name}</span>
                    </div>
                  ))}
                </div>
              </div>
              <div className="flex border-t border-gray-100">
                <button onClick={() => setConfirmIndustry(null)}
                  className="flex-1 px-4 py-3 text-sm font-medium text-gray-600 hover:bg-gray-50 rounded-bl-xl whitespace-nowrap">
                  Cancel
                </button>
                <button onClick={confirmUseExample}
                  className="flex-1 px-4 py-3 text-sm font-medium text-indigo-600 hover:bg-indigo-50 border-l border-gray-100 rounded-br-xl whitespace-nowrap">
                  Confirm
                </button>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}